import { Button, Clickable, P } from '@ahmdigital/ui';
import { includes, map } from 'lodash/fp';
import React from 'react';

import { SANITY_MEDIA_FILES, TILE_VARIANTS } from '../../../ahm-constants/ui';
import constants from '../../../ahm-constants';
import goodStuffGlassesImage from './images/good-stuff-glasses.svg';
import thumbsUpInBandageImage from './images/thumbs-up-in-bandage.svg';
import uncapFpIterator from '../../../../utils/uncap-fp-iterator';

const mapWithIndex = uncapFpIterator(map);

const {
  URLS: {
    HELP: { AGR_CHANGES },
    SALES: {
      CONTACT_US,
      EXPLAINED_HEALTH_INSURANCE_EXPLAINED,
      PRODUCT_LISTING_EXTRAS,
      PRODUCT_LISTING_HOSPITAL,
      REFINE_PRICE,
      TAX,
    },
  },
} = constants;
const NEW_LINE = '\n';
const { COMBINED, EXTRAS, HOSPITAL } = constants.PRODUCT_TYPE;
const { PRODUCT, SCALE } = constants;

// @ts-expect-error - Automatic, Please fix when editing this file
const getCombinedProductsForScale = (scale) => [
  includes(scale, [SCALE.FAMILY, SCALE.SINGLE_PARENT])
    ? PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750_CHOOSABLE_50_TEETH
    : PRODUCT.AMBULANCE_AND_ACCIDENT_BASIC_EXCESS_750_CHOOSABLE_50_TEETH,
  PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750_VALUE_EXTRAS,
  PRODUCT.STARTER_BRONZE_EXCESS_750_VALUE_EXTRAS,
];

// @ts-expect-error - Automatic, Please fix when editing this file
const combineParagraphs = (paragraphs) => (
  <>
    {mapWithIndex(
      // @ts-expect-error - Automatic, Please fix when editing this file
      (paragraph, index) => (
        <P key={index}>{paragraph}</P>
      ),
      paragraphs,
    )}
  </>
);

export default {
  filterProductId: {
    DEFAULT: PRODUCT.STARTER_FLEXI_BASIC_EXCESS_750,
    [EXTRAS]: PRODUCT.BLACK_60_EXTRAS,
    [HOSPITAL]: PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750,
  },
  meta: {
    description:
      'Comparing health insurance is a breeze with ahm. Discover different health cover options for singles, couples, families and single parents today.',
    title: 'Join ahm - Browse Policies & Plans',
  },
  sections: {
    'common-questions': {
      questions: [
        {
          answerRaw: combineParagraphs([
            'Private health insurance helps pay for health care that Medicare doesn’t cover. It’s split into two categories – hospital and extras cover. Depending on your needs, you can buy hospital and extras cover separately or packaged together. We’ve got heaps of different cover options, from basic to more extensive plans.',
            <Clickable to={REFINE_PRICE}>Find and compare covers</Clickable>,
          ]),
          question: 'What is private health insurance?',
        },
        {
          answerRaw: combineParagraphs([
            'Hospital products help with the bill for included treatments or services when you’re admitted to hospital as a private patient. Different products include cover for different procedures, so always make sure you check out what’s included, as well as any excess that might apply.',
            <Button to={PRODUCT_LISTING_HOSPITAL}>Compare our hospital cover</Button>,
            'Extras cover helps with the bill for things like dental check-ups, physio appointments, optical and more.',
            <Button to={PRODUCT_LISTING_EXTRAS}>Compare our extras cover</Button>,
          ]),
          question: 'Hospital or Extras Cover - what’s the difference?',
        },
        {
          answerRaw: combineParagraphs([
            'Aside from helping with the bill for different health-related products and procedures, private health insurance can generally give you more choice. For example, having a policy with a health insurer like ahm means that if you need to go to hospital, you can be treated as a private patient, so you can generally choose the doctors and specialists you see – and even the hospital itself.',
            <>
              If you earn over the <Clickable to={AGR_CHANGES}>minimum threshold amount</Clickable> per year and don’t
              have appropriate hospital cover for you and all your dependants over the whole year, you may be charged
              the Medicare Levy Surcharge (MLS) at tax time. Join ahm hospital cover and you could save on the surcharge
              – simple.
            </>,
            <Clickable to={TAX}>Find out more here</Clickable>,
          ]),
          question: 'What are the benefits of having private health insurance?',
        },
        {
          answerRaw: combineParagraphs([
            <>
              Switching to ahm is easy. Simply{' '}
              <Clickable to={REFINE_PRICE}>join a cover that suits your needs</Clickable> and leave the paperwork to us.
            </>,
          ]),
          question: 'How do I switch to ahm health insurance?',
        },
        {
          answerRaw: combineParagraphs([
            <>
              Keen to join but still stuck on a few details? No stress - just{' '}
              <Clickable to={CONTACT_US}>contact our friendly team</Clickable>.
            </>,
            'For more on the ins and outs of health insurance, check out this handy resource.',
            <Clickable to={EXPLAINED_HEALTH_INSURANCE_EXPLAINED}>Health Insurance Explained</Clickable>,
          ]),
          question: 'How do I get in touch?',
        },
      ],
      title: 'Common questions',
    },
    'content-tiles': {
      tiles: [
        [
          {
            image: {
              alt: '',
              src: goodStuffGlassesImage.src,
            },
            subtitle: `Once you’ve found your perfect cover, joining is easy – just make sure you have your Medicare Card and bank details handy, and you’re set to go.${NEW_LINE}And if you’re not happy, get in touch within the 30-day cooling off period - we can cancel your cover, or have a chat about other cover options. As long as you haven’t made any claims we’ll refund your premiums.`,
            title: 'Join in a matter of minutes',
            variant: TILE_VARIANTS.VERTICAL,
          },
          {
            ctas: [
              {
                name: 'Browse our cover',
                url: constants.URLS.SALES.REFINE_PRICE,
              },
            ],
            image: {
              alt: '',
              src: thumbsUpInBandageImage.src,
            },
            subtitle:
              'Just tell us the name of your current health insurer when you sign up, and we’ll take care of the rest. Don’t worry, we’ll let them down gently.',
            title: 'Switching health funds? We‘ll handle the breakup',
            variant: TILE_VARIANTS.VERTICAL,
          },
        ],
      ],
    },
    'find-cover': {
      combinedProductBlurbs: {
        [PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750_CHOOSABLE_50_TEETH]:
          'Basic hospital essentials and extras with 50% back on dental needs up to annual limit',
        [PRODUCT.AMBULANCE_AND_ACCIDENT_BASIC_EXCESS_750_CHOOSABLE_50_TEETH]:
          'Starter hospital cover for accidents and extras with 50% back on dental needs up to annual limit',
        [PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750_VALUE_EXTRAS]:
          'Bronze hospital plus and popular extras services including, dental, psychology, physio and chiro',
        [PRODUCT.STARTER_BRONZE_EXCESS_750_VALUE_EXTRAS]:
          'Bronze hospital and popular extras services including, dental, psychology, physio and chiro',
      },
      ctaLinks: {
        [COMBINED]: constants.URLS.SALES.PRODUCT_LISTING_PACKAGES,
        [EXTRAS]: constants.URLS.SALES.PRODUCT_LISTING_EXTRAS,
        [HOSPITAL]: constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL,
      },
      ctaText: {
        [COMBINED]: 'See package options',
        [EXTRAS]: 'Compare all options',
        [HOSPITAL]: 'Compare all options',
      },
      productOptions: {
        [COMBINED]: {
          [SCALE.COUPLE]: getCombinedProductsForScale(SCALE.COUPLE),
          [SCALE.FAMILY]: getCombinedProductsForScale(SCALE.FAMILY),
          [SCALE.SINGLE]: getCombinedProductsForScale(SCALE.SINGLE),
          [SCALE.SINGLE_PARENT]: getCombinedProductsForScale(SCALE.SINGLE_PARENT),
        },
        [EXTRAS]: {
          [SCALE.COUPLE]: [PRODUCT.BLACK_60_EXTRAS, PRODUCT.VALUE_EXTRAS, PRODUCT.FAMILY_EXTRAS],
          [SCALE.FAMILY]: [PRODUCT.BLACK_60_EXTRAS, PRODUCT.FAMILY_EXTRAS, PRODUCT.SUPER_EXTRAS],
          [SCALE.SINGLE]: [PRODUCT.BLACK_60_EXTRAS, PRODUCT.VALUE_EXTRAS, PRODUCT.LIFESTYLE_EXTRAS],
          [SCALE.SINGLE_PARENT]: [PRODUCT.BLACK_60_EXTRAS, PRODUCT.VALUE_EXTRAS, PRODUCT.FAMILY_EXTRAS],
        },
        [HOSPITAL]: {
          [SCALE.COUPLE]: [
            PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750,
            PRODUCT.STARTER_BRONZE_EXCESS_750,
            PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750,
          ],
          [SCALE.FAMILY]: [
            PRODUCT.CORE_BRONZE_PLUS_EXCESS_750,
            PRODUCT.ESSENTIALS_SILVER_PLUS_EXCESS_750,
            PRODUCT.CORE_SILVER_PLUS_EXCESS_750,
          ],
          [SCALE.SINGLE]: [
            PRODUCT.CORE_HOSPITAL_BASIC_PLUS_EXCESS_750,
            PRODUCT.SIMPLE_BRONZE_PLUS_EXCESS_750,
            PRODUCT.STARTER_BRONZE_EXCESS_750,
          ],
          [SCALE.SINGLE_PARENT]: [
            PRODUCT.CORE_BRONZE_PLUS_EXCESS_750,
            PRODUCT.STARTER_BRONZE_EXCESS_750,
            PRODUCT.ESSENTIALS_SILVER_PLUS_EXCESS_750,
          ],
        },
      },
      title: 'Find health cover that suits your needs',
    },
    'hero-banner': {
      cta: {
        title: 'Find cover',
        to: '/refine-price',
      },
      subtitle: [
        {
          _type: 'block',
          children: [
            {
              _key: '0',
              _type: 'span',
              text: 'Discombobulated by choosing health insurance? With ahm, You’re Good with simple, smart and affordable insurance. ',
            },
          ],
          style: 'normal',
        },
      ],
      title: 'Health insurance that’s more black and white',
      video: SANITY_MEDIA_FILES.GLITCH_VIDEO,
    },
  },
};
